import React, { FC, useEffect, useMemo } from 'react'

import { Box, Stack, TablePagination, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { EmptyAccountData } from '../../components/EmptyAccountData'
import { EmptyTable } from '../../components/Table/EmptyTable'
import Table from '../../components/Table/Table'
import { CommonTableColumnsEnum } from '../../components/Table/types'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { SortDirectionCapital } from '../../types'
import { getTablePaginationDisplayedRows } from '../../utils'

import { TradingLogHeader } from './TradingLogHeader'
import {
  fetchTrades,
  updateColumnOrder,
  updateOrderBy,
  updatePage,
  updatePageSize,
  updateSortOrder,
} from './TradingLogSlice'
import { Trade } from './types'

const TradingLogPage: FC = () => {
  const { spacing } = useTheme()
  const { t } = useTranslation()

  const trades = useAppSelector((state) => state.tradingLog.trades)
  const { page, perPage, itemCount, columns, order, orderBy } = useAppSelector(
    (state) => state.tradingLog.pagination,
  )
  const { currentAccount, userAccounts, period } = useAppSelector(
    ({ userAccounts }) => userAccounts,
  )
  const [startDate, endDate] = period

  const noTradesMessage: string = t(
    'tradingLogPage.emptyState.noTradesSelectedTime',
    'There are no trades to display for the selected time. Try to select different filters',
  )

  const searchPeriod = useMemo(
    () => (startDate && endDate ? [startDate, endDate] : [null, null]),
    [startDate, endDate],
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (currentAccount?.id) {
      dispatch(
        fetchTrades({
          page,
          perPage,
          columns: [CommonTableColumnsEnum.id, CommonTableColumnsEnum.symbol, ...columns],
          period: searchPeriod,
          account: currentAccount.id,
          order,
          orderBy: orderBy || CommonTableColumnsEnum.id,
          fromCalendar: false,
        }),
      )
    }
  }, [page, perPage, columns, order, orderBy, currentAccount?.id, period])

  const handlePageChange = (e: unknown, newPage: number) => {
    dispatch(updatePage(newPage + 1))
  }
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updatePageSize(parseInt(e.target.value, 10)))
    dispatch(updatePage(1))
  }

  const handleColumnReorder = (startIndex: number, endIndex: number) => {
    const newColumnOrder = Array.from(columns)
    const [removed] = newColumnOrder.splice(startIndex, 1)
    newColumnOrder.splice(endIndex, 0, removed)
    dispatch(updateColumnOrder(newColumnOrder))
  }

  const handleSort = (sortColumn: keyof Trade) => {
    if (orderBy === sortColumn) {
      dispatch(
        updateSortOrder(
          order === SortDirectionCapital.asc ? SortDirectionCapital.desc : SortDirectionCapital.asc,
        ),
      )
    } else {
      dispatch(updateOrderBy(sortColumn))
    }
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" flex={1}>
      {itemCount ? <TradingLogHeader /> : null}

      {Boolean(userAccounts.length) ? (
        !itemCount ? (
          <EmptyTable message={noTradesMessage} />
        ) : (
          <>
            <Stack p={spacing(2)} direction={'column'} flex={1}>
              <Table
                columnOrder={columns}
                sortingColumn={orderBy}
                sortingDirection={order}
                onColumnReorder={handleColumnReorder}
                onSort={handleSort}
                displayedTrades={trades}
              />

              <TablePagination
                labelRowsPerPage={t('tradingLogPage.pagination.perPage', 'Per page')}
                labelDisplayedRows={getTablePaginationDisplayedRows(t)}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                component="div"
                count={itemCount}
                rowsPerPage={perPage}
                page={page - 1}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePageSizeChange}
              />
            </Stack>
          </>
        )
      ) : (
        <EmptyAccountData />
      )}
    </Box>
  )
}

export default TradingLogPage
